import { useCallback, useState } from 'react'

import get from 'lodash/get'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'

import { blankFromTemplate, fillDefaultKeys, setImmutable } from './utilities'

/**
 * Shared editing primitives for a document built from extraction JSON: setting a field by path,
 * adding a blank item to a list (templated off its first sibling, or off the section's default
 * keys if the list is empty), removing an item from a list, and tracking which collapsible node
 * IDs are expanded. `setDocument` is the state setter for whatever object is being edited — the
 * full extraction document in IepExtractionEditorDialog, or a single section item in
 * IepSectionItemEditDrawer — so the same logic applies to both without depending on which one it
 * is.
 */
const useExtractionItemEditor = setDocument => {
  const [expandedIds, setExpandedIds] = useState(() => new Set())

  const handleChange = useCallback(
    (path, value) => {
      setDocument(current => setImmutable(current, path, value))
    },
    [setDocument]
  )

  const handleAddItem = useCallback(
    (path, insertIndex) => {
      setDocument(current => {
        if (!current) {
          return current
        }

        const list = get(current, path)
        const sectionKey = [...path].reverse().find(isString) || path[0]
        let template
        if (Array.isArray(list) && list.length > 0) {
          template = blankFromTemplate(list[0])
        } else {
          template = fillDefaultKeys({}, sectionKey)
          if (Object.keys(template).length === 0) {
            template = ''
          }
        }

        if (isObject(template)) {
          template = fillDefaultKeys(template, sectionKey)

          // `blankFromTemplate` empties every string field, including `id` — but the backend
          // requires `id` to be populated on create (see createBlankItem in ./utilities), so a
          // brand-new item needs a real value there rather than "".
          if ('id' in template) {
            const uuidVal = crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15)
            template.id = uuidVal
            if ('uniqueIdentifier' in template) {
              template.uniqueIdentifier = uuidVal
            }
          }
        }

        let nextList
        if (isNumber(insertIndex) && Array.isArray(list)) {
          nextList = [...list]
          nextList.splice(insertIndex + 1, 0, template)
        } else {
          nextList = [...(list || []), template]
        }

        return setImmutable(current, path, nextList)
      })
    },
    [setDocument]
  )

  const handleRemoveItem = useCallback(
    (path, index) => {
      setDocument(current => {
        if (!current) {
          return current
        }
        const list = get(current, path)
        if (Array.isArray(list)) {
          return setImmutable(
            current,
            path,
            list.filter((_, itemIndex) => itemIndex !== index)
          )
        }
        return current
      })
    },
    [setDocument]
  )

  const isExpanded = useCallback(id => expandedIds.has(id), [expandedIds])

  const toggleExpanded = useCallback(id => {
    setExpandedIds(current => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return {
    expandedIds,
    setExpandedIds,
    handleChange,
    handleAddItem,
    handleRemoveItem,
    isExpanded,
    toggleExpanded
  }
}

export { useExtractionItemEditor }
